import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { vars } from 'ui/styles/vars.css'

import { Box, BoxProps } from '../Box/Box'
import { MotionBox } from '../Motion/MotionComponents'
import { CardOpener } from '../Overlay/CardOpener'
import { CardOpenerContext } from '../Overlay/CardOpenerContext'

export type Props<T> = {
    value: T
    options: T[]
    onChange?: (value: T) => void
    renderOption: (item: T, selected?: boolean) => React.ReactNode
    renderButton?: (item: T) => React.ReactNode
    getKey: (item: T) => string
    scrollHeight?: number
} & Pick<
    BoxProps,
    | 'padding'
    | 'paddingX'
    | 'paddingY'
    | 'border'
    | 'borderRadius'
    | 'justifyContent'
    | 'alignItems'
    | 'centerContent'
    | 'color'
    | 'background'
    | 'minWidth'
>

export const PopupMenu = <T,>(props: Props<T>) => {
    const {
        options = [],
        getKey,
        renderOption,
        renderButton,
        onChange,
        value: _value,
        ...boxProps
    } = props
    const [value, setValue] = useState(_value)

    const onSelect = useCallback(
        (value: T) => {
            setValue(value)
            onChange?.(value)
        },
        [onChange],
    )

    const valueRef = useRef(_value)
    valueRef.current = value
    useEffect(() => {
        if (valueRef.current !== _value) {
            setValue(_value)
        }
    }, [_value])

    return (
        <CardOpener
            placement="dropdown"
            render={
                <Box paddingY="sm">
                    <MotionBox
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        background="level2"
                        borderRadius="sm"
                        boxShadow="dropdown"
                        overflow={props.scrollHeight ? 'scroll' : 'hidden'}
                        style={{ maxHeight: props.scrollHeight }}
                        position="relative"
                    >
                        {options.map((t, index) => {
                            return (
                                <PopupMenuOption
                                    renderOption={(option) =>
                                        renderOption(option, getKey(option) === getKey(value))
                                    }
                                    option={t}
                                    tabIndex={0}
                                    key={getKey(t)}
                                    data-testid={`popup-menu-option-${getKey(t)}`}
                                    paddingY="sm"
                                    paddingTop={index === 0 ? 'md' : undefined}
                                    paddingBottom={index === options.length - 1 ? 'md' : undefined}
                                    onSelectOption={onSelect}
                                />
                            )
                        })}
                    </MotionBox>
                    {/* cosmetic gradient overlay to mask bottom for scroll */}
                    {props.scrollHeight && (
                        <Box absoluteFill pointerEvents="none" paddingY="sm">
                            <Box
                                height="100%"
                                width="100%"
                                borderRadius="sm"
                                overflow="hidden"
                                style={{
                                    background: `linear-gradient(to bottom, transparent 80%, ${vars.color.background.level2} 100%)`,
                                }}
                            />
                        </Box>
                    )}
                </Box>
            }
        >
            {({ triggerProps }) => (
                <Box
                    hoverable
                    paddingX={props.paddingX ?? props.padding ?? 'md'}
                    paddingY={props.paddingY ?? props.padding ?? 'md'}
                    as="button"
                    style={{ userSelect: 'none' }}
                    borderRadius="sm"
                    background="level2"
                    {...boxProps}
                    {...triggerProps}
                >
                    {renderButton ? renderButton(value) : renderOption(value)}
                </Box>
            )}
        </CardOpener>
    )
}

const PopupMenuOption = <T,>(
    props: {
        onSelectOption: (item: T) => void
        option: T
        renderOption: (item: T) => React.ReactNode
    } & BoxProps,
) => {
    const { onSelectOption, option, renderOption, ...boxProps } = props
    const ctx = useContext(CardOpenerContext)
    return (
        <Box
            hoverable
            cursor="pointer"
            paddingX="md"
            paddingY="sm"
            role="option"
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ' || e.key === 'Space') {
                    // Prevent page scroll when Space is pressed
                    if (e.key === ' ' || e.key === 'Space') {
                        e.preventDefault()
                    }
                    onSelectOption(option)
                    ctx?.closeCard()
                }
            }}
            onClick={() => {
                onSelectOption(option)
                ctx?.closeCard()
            }}
            {...boxProps}
        >
            {renderOption(option)}
        </Box>
    )
}
